import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_02: GgxTourDefinition = {
    key: '[RISK-02] Understanding Risks in GenAI',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 6,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'What can go wrong with GenAI, and who says so',
        subtitle:
            'Every AI system carries risk, and generative AI raises the stakes: it runs at ' +
            'scale, it does not give the same answer twice, and it can produce something that ' +
            'sounds completely convincing and is wrong. Regulators and standards bodies have ' +
            'each drawn up their own map of those risks. This tour gives you a short video ' +
            'overview, shows how the main frameworks compare, and then opens the credit card ' +
            'chatbot to see how GGX turns that map into something a team can track.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You now have the lay of the land: the risks that come with GenAI, how the NIST AI ' +
            'RMF, OECD, EU and US frameworks group them, how the Singapore MAS principles put ' +
            'them in plain words, and where they land in GGX, as categories and risk ' +
            'attributes on every pipeline. Identifying, measuring and mitigating each risk is ' +
            'covered in the tours that follow. Next, Understanding Risk Assessment tools in ' +
            'GGX takes two of these risks, hallucination and prompt injection, and shows how ' +
            'this pipeline handled them.',
        learned: [
            '**NIST, OECD, EU and US** frameworks group the same GenAI risks.',
            'The **MAS principles** put them in plain words.',
            'In GGX they land as **categories and risk attributes** on every pipeline.',
        ],
    },
    chapters: [
        {
            name: 'Find the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'search',
            steps: [2, 3, 4],
        },
        {
            name: 'Know the risks',
            blurb: 'A video overview and how the frameworks compare',
            icon: 'cap',
            steps: [5, 6, 7],
        },
        {
            name: 'See them tracked',
            blurb: 'Where the risks live on a pipeline in GGX',
            icon: 'map',
            steps: [8, 9, 10],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: 'Start from the search box',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will look at risk on a real pipeline, and the quickest way to any object ' +
                'in GGX is the global search. It finds pipelines, prompts, reports and portals ' +
                'by name from any page, and <strong>Ctrl+K</strong> opens it too. Click ' +
                '<strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            title: 'Understanding risks in GenAI',
            whatHappens: 'next, how the major frameworks organise these risks.',
            icon: 'cap',
            description:
                'Most AI risks are not new. What GenAI changes is how far and how fast they ' +
                'spread: a model answers thousands of customers at once, it does not give the ' +
                'same answer twice, and when it is wrong it is wrong fluently. This short video ' +
                'walks through the known risks and why each one is sharper for generative ' +
                'systems. Watch it, then click Continue.',
            video: {
                src: 'https://genguardx.ai/tours/risk-02-risks-in-genai.mp4',
                title: 'Understanding Risks in GenAI',
            },
        },
        {
            title: 'There is no single list of AI risks',
            whatHappens: 'next, one framework put in plain words.',
            icon: 'cap',
            description:
                'Regulators and standards bodies each carve up AI risk their own way. The ' +
                'table lines four of them up side by side:' +
                '<ul data-ggx-list>' +
                '<li><strong>NIST AI RMF</strong>: accuracy, reliability, robustness and ' +
                'bias.</li>' +
                '<li><strong>OECD</strong>: leads with robustness, security and safety.</li>' +
                '<li><strong>EU</strong>: adds data governance and human oversight.</li>' +
                '<li><strong>US EO 13960</strong>: systems that are accurate, secure and ' +
                'accountable.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">The words differ, the concerns overlap ' +
                'heavily. Pick one taxonomy for your organisation and map the others to it, ' +
                'rather than tracking four lists.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-02-frameworks-compared.png',
                title: 'NIST AI RMF, OECD, EU and US EO 13960 compared',
                caption: 'Risk attributes across four frameworks.',
                sourceUrl:
                    'https://www.nist.gov/system/files/documents/2021/10/15/taxonomy_AI_risks.pdf',
            },
        },
        {
            title: 'One framework in plain words',
            whatHappens: 'next, where these risks live in GGX.',
            icon: 'cap',
            description:
                "The Monetary Authority of Singapore's whitepaper is a good example of a " +
                'practical taxonomy. Read it as a checklist a bank would actually run through ' +
                'before putting a chatbot in front of customers. So how do you track all of ' +
                'this for a pipeline in GGX? Click Continue.',
            benefits: [
                'Fairness and bias',
                'Ethics and impact',
                'Accountability and governance',
                'Transparency and explainability',
                'Legal and regulatory',
                'Monitoring and stability',
                'Cyber and data security',
            ],
            image: {
                src: 'https://genguardx.ai/tours/risk-02-mas-principles.png',
                title: 'Singapore MAS whitepaper principles',
                caption: 'The seven principle areas from the MAS whitepaper.',
                sourceUrl:
                    'https://www.mas.gov.sg/-/media/mas/news/media-releases/2023/executive-summary---emerging-risks-and-opportunities-of-generative-ai-for-banks.pdf#page=5',
            },
        },
        {
            target: '[test-id="tab-item-Risk Assessment"]',
            scrollIntoView: true,
            title: 'Every pipeline carries its risk record',
            whatHappens: 'the Risk Assessment tab opens with the risk categories collapsed.',
            icon: 'map',
            description:
                'For each pipeline, the <strong>Risk Assessment</strong> tab is where the ' +
                'risks that apply are named and tracked, with a record of how each was ' +
                'measured or reduced. Click <strong>Risk Assessment</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Risk Assessment',
        },
        {
            target: '[test-id="risk-category-header-Accuracy"]',
            climbTo: '.p-panel-header',
            scrollIntoView: true,
            title: 'The frameworks, boiled down to four categories',
            whatHappens: 'the Accuracy category expands to list its risk attributes.',
            icon: 'map',
            description:
                'Here the sandbox uses a focused set: <strong>Accuracy</strong>, ' +
                '<strong>Stability</strong>, <strong>Ethics</strong> and ' +
                '<strong>Vulnerability</strong>, each with the risk attributes that GGX can ' +
                'measure through automated evaluations and human testing, and control through ' +
                'model design and guardrails.' +
                '<aside data-ggx-use="Tip">Your organisation can reshape these categories and ' +
                'attributes to match its own taxonomy from the <strong>Configure</strong> ' +
                'button on the right.</aside>' +
                'Click <strong>Accuracy</strong> to see its attributes.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Accuracy',
        },
        {
            target: '[test-id="risk-details"]',
            targetText: 'In-scope accuracy',
            frame: true,
            scrollIntoView: true,
            title: 'One attribute, one record',
            whatHappens: 'the tour wraps up.',
            icon: 'map',
            description:
                'Each attribute has a short definition and, below it, the record the team ' +
                'keeps: how the risk was mitigated, which evaluations measured it, and a ' +
                'comment explaining the reasoning and what is still open. Click through the ' +
                'other attributes on the left, and open the other categories, to see the full ' +
                'picture for this chatbot. Click Continue to finish.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
